/**
 * ProsumerWizard - Main wizard component for prosumer calculator.
 *
 * Uses Mantine Stepper with 4 steps. Renders the correct step component
 * based on current step from useProsumerForm.
 */

import { Stepper, Paper, Stack } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconHome, IconBolt, IconSolarPanel, IconChartBar } from '@tabler/icons-react';
import { useProsumerForm } from '../../hooks/useProsumerForm';
import { WizardNavigation } from './WizardNavigation';
import { ProfileStep } from '../steps/ProfileStep';
import { ConsumptionStep } from '../steps/ConsumptionStep';
import { DevicesStep } from '../steps/DevicesStep';
import { ResultsStep } from '../steps/ResultsStep';
import { STEP_CONFIG } from '../../types';

const STEP_ICONS = [
  <IconHome size={18} key="home" />,
  <IconBolt size={18} key="bolt" />,
  <IconSolarPanel size={18} key="solar" />,
  <IconChartBar size={18} key="chart" />,
];

export function ProsumerWizard() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const {
    form,
    currentStep,
    currentStepIndex,
    isOnResults,
    navigation,
  } = useProsumerForm();

  const isFirstStep = currentStepIndex === 0;
  const isLastInputStep = currentStep === 'devices';

  const handleSkipDevices = () => {
    // Disable all devices and go to results
    form.setFieldValue('hasPV', false);
    form.setFieldValue('hasEV', false);
    form.setFieldValue('hasHeatPump', false);
    navigation.goToNextStep();
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'profile':
        return <ProfileStep form={form} />;
      case 'consumption':
        return <ConsumptionStep form={form} />;
      case 'devices':
        return <DevicesStep form={form} />;
      case 'results':
        return <ResultsStep form={form} />;
      default:
        return null;
    }
  };

  return (
    <Stack gap="lg">
      <Stepper
        active={currentStepIndex}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        onStepClick={(step) => {
          const targetStep = STEP_CONFIG[step];
          if (targetStep) {
            navigation.goToStep(targetStep.key);
          }
        }}
      >
        {STEP_CONFIG.map((step, idx) => (
          <Stepper.Step
            key={step.key}
            label={step.label}
            description={step.description}
            icon={STEP_ICONS[idx]}
          />
        ))}
      </Stepper>

      <Paper p="md" withBorder radius="md">
        {renderCurrentStep()}
      </Paper>

      <WizardNavigation
        currentStep={currentStep}
        isFirstStep={isFirstStep}
        isLastInputStep={isLastInputStep}
        isOnResults={isOnResults}
        onNext={navigation.goToNextStep}
        onPrevious={navigation.goToPreviousStep}
        onSkipDevices={handleSkipDevices}
        onRestart={navigation.restart}
      />
    </Stack>
  );
}
